// The "Contacts & outreach" block of a client update meeting (CRM spec 2026-11-03): the
// client's CRM counts plus what happened in the meeting period. Computed live from CRM data.
// Client-facing: every output object is built field by field from a whitelist (name,
// organization, role, status, activity text) — a contact's email and phone never get in.
import { localDate } from "./period.js";
import { STATUS_META } from "../crm/crmLogic.js";

const localDay = (v) => {
  if (!v) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : localDate(d);
};

export function computeCrm(tracks, contacts, { periodStart, periodEnd, hiddenEventIds = [] } = {}) {
  const byId = new Map((contacts || []).map((c) => [c.id, c]));
  const hidden = new Set(hiddenEventIds);
  const live = (tracks || []).filter((t) => byId.has(t.contactId));
  const counts = { contacts: live.length, lead: 0, inProgress: 0, won: 0 };
  const events = [];
  let hiddenCount = 0;
  for (const t of live) {
    if (t.status === "lead") counts.lead += 1;
    else if (t.status === "in_progress") counts.inProgress += 1;
    else if (t.status === "won") counts.won += 1;
    const c = byId.get(t.contactId);
    const who = { name: String(c.name || ""), organization: String(c.organization || ""), role: String(c.role || "") };
    const push = (e) => {
      const date = localDay(e.at);
      if (!date || date < periodStart || date > periodEnd) return;
      if (hidden.has(e.id)) { hiddenCount += 1; return; }
      events.push({ ...e, date, ...who });
    };
    (t.statusHistory || []).forEach((h, i) => {
      if (!h?.id) return;
      push({ id: String(h.id), at: h.at, kind: i === 0 ? "new" : "status", status: String(h.status || ""), statusLabel: STATUS_META[h.status]?.label || String(h.status || "") });
    });
    for (const a of t.activity || []) {
      if (a?.id) push({ id: String(a.id), at: a.at, kind: "activity", text: String(a.text || "") });
    }
  }
  events.sort((a, b) => (a.at === b.at ? 0 : a.at < b.at ? 1 : -1));
  return { counts, events: events.map(({ at, ...e }) => e), hiddenCount };
}

// The client-facing text of one event. Reads only whitelisted fields.
export function crmEventText(e) {
  const name = String(e?.name || "");
  const detail = [e?.organization, e?.role].filter(Boolean).map(String).join(", ");
  if (e?.kind === "new") return { headline: `New lead: ${name}`, detail, text: "" };
  if (e?.kind === "status") return { headline: `${name} → ${String(e.statusLabel || e.status || "")}`, detail, text: "" };
  return { headline: name, detail, text: String(e?.text || "") };
}

// What a client may see of the block for this meeting (`view` = publicView output), or null
// when it doesn't render: kickoff, hidden, no CRM contacts. Hidden events are filtered again
// here so a renderer never relies on its caller having done it.
export function visibleCrm(crm, view) {
  if (!crm || !view || view.type === "kickoff" || view.crm?.hidden) return null;
  if (!crm.counts || !crm.counts.contacts) return null;
  const hidden = new Set(view.crm?.hiddenEventIds || []);
  const c = crm.counts;
  return {
    counts: { contacts: Number(c.contacts) || 0, lead: Number(c.lead) || 0, inProgress: Number(c.inProgress) || 0, won: Number(c.won) || 0 },
    events: (crm.events || []).filter((e) => !hidden.has(e.id)).map((e) => ({ id: String(e.id), date: String(e.date || ""), ...crmEventText(e) })),
  };
}
